import { useState, createContext, useContext, useCallback, useMemo, useEffect, useRef } from 'react';
import { trackStepEntered, trackAbandon } from '@/lib/bookingTelemetry';
import { normalizeBookingDoctor, slotsApi, type SlotHoldResponse } from '@/features/booking';
import type { 
  DoctorDetailResponse, 
  TimeSlot, 
  ReimbursementEstimation,
  DoctorSearchParams,
} from '@/lib/types';

/**
 * Verrou de créneau tel que le tunnel le manipule.
 *
 * <p>Il n'est PAS persisté dans `localStorage` : un verrou survit au rechargement côté serveur,
 * mais le contexte restauré d'un ancien build pourrait porter un verrou expiré depuis longtemps
 * et afficher un compte à rebours faux. On le récupère donc à la volée à la lecture des
 * disponibilités (le serveur renvoie les créneaux que ce patient tient encore).
 */
export interface SlotHold {
  holdId: string;
  slotId: string;
  expiresAt: string;
  remainingSeconds: number;
  status: string;
}

/**
 * Current step in the booking workflow
 */
export type BookingStep = 
  | 'SEARCH'
  | 'RESULTS'
  | 'DETAILS'
  | 'CALENDAR'
  | 'SLOTS'
  | 'QUESTIONS'
  | 'SUMMARY'
  | 'CONFIRMATION';

/**
 * Booking context state
 */
/**
 * Canal de consultation — choix du parcours, pas un détail d'affichage.
 *
 * <p>Défini ici et non dans {@code Step4_Calendar} : le type doit être partagé par toutes les
 * étapes du tunnel, sinon le choix de l'une ne peut pas être lu par les autres.
 */
export type ConsultationType = 'PHYSICAL' | 'TELECONSULTATION';

interface BookingContextType {
  // Current step
  currentStep: BookingStep;
  goToStep: (step: BookingStep) => void;
  nextStep: () => void;
  prevStep: () => void;
  
  // Search state
  searchParams: DoctorSearchParams | null;
  setSearchParams: (params: DoctorSearchParams) => void;
  
  // Selected doctor
  selectedDoctor: DoctorDetailResponse | null;
  setSelectedDoctor: (doctor: DoctorDetailResponse | null) => void;
  
  // Calendar state
  selectedDate: Date | null;
  setSelectedDate: (date: Date | null) => void;
  availableDates: Record<string, 'available' | 'partial' | 'unavailable'>;
  setAvailableDates: (dates: Record<string, 'available' | 'partial' | 'unavailable'>) => void;
  
  // Selected slot
  selectedSlot: TimeSlot | null;
  setSelectedSlot: (slot: TimeSlot | null) => void;

  /**
   * Verrou temporaire du créneau sélectionné.
   *
   * <p><b>Pourquoi il vit dans le contexte :</b> le patient tient une place pendant qu'il
   * remplit le tunnel (questions, récapitulatif, confirmation). Ce verrou doit être visible de
   * toutes les étapes : l'étape 5 le pose, l'étape 8 vérifie qu'il tient encore, et un retour
   * arrière doit le libérer pour ne pas bloquer les autres patients.
   *
   * <p>{@code null} signifie « pas de verrou » — soit parce qu'aucun créneau n'est choisi, soit
   * parce que la pose a échoué (créneau déjà tenu par un autre, réseau). L'absence de verrou
   * n'empêche pas de continuer : la réservation reste possible, elle sera simplement exposée à
   * une course.
   */
  slotHold: SlotHold | null;

  /**
   * Tient le créneau choisi. Renvoie le message d'erreur si la place est déjà tenue par un
   * autre patient, {@code null} en cas de succès.
   */
  holdSlot: (slot: TimeSlot) => Promise<string | null>;

  /** Libère le verrou courant (retour arrière, changement de créneau, abandon). */
  releaseHold: () => Promise<void>;

  /**
   * Oublie le verrou localement, sans appel serveur.
   *
   * <p>Utilisé après une réservation réussie : le serveur a déjà fermé le verrou en
   * {@code CONSUMED} dans la même transaction. Rappeler {@code DELETE} produirait un 404
   * inutile (et un échec silencieux dans la console du patient).
   */
  forgetHold: () => void;

  /**
   * Canal de consultation choisi à l'étape calendrier : PHYSICAL ou TELECONSULTATION.
   *
   * <p><b>Défaut corrigé :</b> ce choix était un état LOCAL à {@code Step4_Calendar}. Il n'était
   * donc jamais transmis aux étapes suivantes, et {@code Step5_Slots} affichait les créneaux de
   * TOUS les canaux. Résultat vérifié en production : un praticien avec deux actes renvoyait
   * 140 créneaux pour 70 horaires — chaque heure apparaissait deux fois, une fois en cabinet et
   * une fois en vidéo, sans que le patient puisse savoir laquelle correspondait à son choix.
   *
   * <p>Le choix doit vivre dans le contexte du tunnel : c'est une décision du parcours, pas un
   * détail d'affichage d'une étape.
   */
  consultationType: ConsultationType;
  setConsultationType: (type: ConsultationType) => void;

  /**
   * Acte choisi à l'étape calendrier (consultation 30 min, téléconsultation 20 min…).
   *
   * <p><b>Défaut corrigé :</b> le `<select>` « Type d'acte » de {@code Step4_Calendar} avait
   * un {@code defaultValue} et AUCUN {@code onChange}. Le choix n'était stocké nulle part et
   * ne filtrait rien : un patient qui sélectionnait « Teleconsultation (20 min) » réservait
   * l'acte par défaut, avec sa durée et son tarif. Deux actes = deux prix ; le patient
   * pouvait donc payer autre chose que ce qu'il avait demandé.
   */
  selectedActTypeId: string | null;
  setSelectedActTypeId: (id: string | null) => void;
  
  // Pre-consultation answers
  preConsultationAnswers: Record<string, string>;
  setPreConsultationAnswers: (answers: Record<string, string>) => void;
  
  // Reimbursement estimation
  reimbursementEstimation: ReimbursementEstimation | null;
  setReimbursementEstimation: (estimation: ReimbursementEstimation | null) => void;
  
  // Booking confirmation
  bookingConfirmed: boolean;
  bookingId: string | null;
  setBookingConfirmed: (confirmed: boolean, id?: string) => void;
  
  // Reset the entire workflow
  reset: () => void;
}

// Create context
const BookingContext = createContext<BookingContextType | null>(null);

/**
 * Clé de persistance du tunnel — issue #60 (S1-15).
 *
 * Le contexte ne vivait qu'en mémoire : un rechargement de page (ou un retour depuis
 * un onglet de connexion) effaçait praticien, créneau et réponses aux questions, et
 * le patient devait tout refaire. Le tunnel est donc sauvegardé localement.
 */
const STORAGE_KEY = 'zimed_booking_context';

/** Les huit étapes du tunnel — source unique de vérité pour valider une restauration. */
const BOOKING_STEPS: readonly BookingStep[] = [
  'SEARCH',
  'RESULTS',
  'DETAILS',
  'CALENDAR',
  'SLOTS',
  'QUESTIONS',
  'SUMMARY',
  'CONFIRMATION',
];

/**
 * Vrai si la valeur lue dans le stockage est une étape réelle du tunnel.
 *
 * <p><b>Défaut constaté en production :</b> `loadPersisted()` ne validait rien. Un
 * `currentStep` inconnu (ancienne version de l'application, données d'un autre build, valeur
 * modifiée à la main) faisait `stepComponents[currentStep]` → `undefined`, et React levait
 * « Element type is invalid » (erreur #130) : le `<div id="root">` était vidé, donc une page
 * BLANCHE. Reproduit en production en injectant `currentStep: "PAYMENT"` dans le stockage.
 *
 * <p>Une donnée de stockage n'est pas une donnée de confiance : elle survit aux versions du
 * code. On la valide donc avant de s'en servir.
 */
function isValidStep(value: unknown): value is BookingStep {
  return typeof value === 'string' && (BOOKING_STEPS as readonly string[]).includes(value);
}

interface PersistedBooking {
  currentStep: BookingStep;
  searchParams: DoctorSearchParams | null;
  selectedDoctor: DoctorDetailResponse | null;
  selectedDate: string | null;
  availableDates: Record<string, 'available' | 'partial' | 'unavailable'>;
  selectedSlot: TimeSlot | null;
  preConsultationAnswers: Record<string, string>;
  /**
   * Le canal de consultation doit survivre a un rechargement : un patient qui revient sur le tunnel
   * ne doit pas voir son choix remis a « cabinet » sans le savoir.
   */
  consultationType?: ConsultationType;
  /** L'acte choisi doit survivre a un rechargement, comme le canal. */
  selectedActTypeId?: string | null;
}

function loadPersisted(): Partial<PersistedBooking> | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<PersistedBooking> | null;
    if (!parsed || typeof parsed !== 'object') return null;

    // Une étape inconnue ferait rendre `stepComponents[étape]` = undefined → React #130 →
    // page blanche. On repart proprement sur l'étape 1 plutôt que de planter l'écran.
    if (parsed.currentStep !== undefined && !isValidStep(parsed.currentStep)) {
      clearPersisted();
      return null;
    }

    return parsed;
  } catch {
    // Donnée corrompue : on repart proprement plutôt que de planter le tunnel.
    return null;
  }
}

function savePersisted(state: PersistedBooking) {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Quota plein ou mode privé : le tunnel continue en mémoire.
  }
}

function clearPersisted() {
  if (typeof window === 'undefined') return;
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // sans conséquence
  }
}

/**
 * BookingProvider component
 * 
 * Wraps the booking workflow and provides state management for all steps.
 * 
 * @example
 * ```tsx
 * <BookingProvider>
 *   <BookingWorkflow />
 * </BookingProvider>
 * ```
 */
export function BookingProvider({ children }: { children: React.ReactNode }) {
  // Restauration du tunnel interrompu (rechargement, retour d'un autre onglet).
  const restored = loadPersisted();

  // State
  const [currentStep, setCurrentStep] = useState<BookingStep>(
    restored?.currentStep ?? 'SEARCH'
  );
  const [searchParams, setSearchParams] = useState<DoctorSearchParams | null>(
    restored?.searchParams ?? null
  );
  const [selectedDoctor, setSelectedDoctor] = useState<DoctorDetailResponse | null>(
    // Le tunnel persiste `selectedDoctor` dans localStorage : une entree ecrite par une
    // version anterieure du front peut contenir l'ANCIENNE forme (consultationModes objet,
    // addressLine1). La relire telle quelle faisait lever
    // « consultationModes.includes is not a function » des la restauration.
    // On normalise donc a la lecture, pas seulement a l'ecriture.
    normalizeBookingDoctor(restored?.selectedDoctor)
  );
  const [selectedDate, setSelectedDate] = useState<Date | null>(
    restored?.selectedDate ? new Date(restored.selectedDate) : null
  );
  const [availableDates, setAvailableDates] = useState<Record<string, 'available' | 'partial' | 'unavailable'>>(
    restored?.availableDates ?? {}
  );
  const [consultationType, setConsultationType] = useState<ConsultationType>(
    restored?.consultationType ?? 'PHYSICAL'
  );
  const [selectedActTypeId, setSelectedActTypeId] = useState<string | null>(
    restored?.selectedActTypeId ?? null
  );

  const [selectedSlot, setSelectedSlot] = useState<TimeSlot | null>(
    restored?.selectedSlot ?? null
  );

  /**
   * Verrou du créneau sélectionné.
   *
   * <p>Non persisté (voir {@link SlotHold}) : un verrou restauré d'un ancien build afficherait
   * un compte à rebours faux. Il est reposé à la volée dès que le patient choisit un créneau.
   */
  const [slotHold, setSlotHold] = useState<SlotHold | null>(null);

  /**
   * Le verrou courant, lisible depuis les callbacks sans les recréer à chaque rendu.
   *
   * <p>Sans cette référence, `releaseHold` dépendrait de `slotHold` et changerait d'identité à
   * chaque pose — ce qui déclencherait des effets en boucle dans les étapes qui l'utilisent.
   */
  const slotHoldRef = useRef<SlotHold | null>(null);

  /**
   * Tient un créneau. Renvoie le message d'erreur si la place est tenue par un autre patient.
   *
   * <p><b>Pourquoi on ne bloque pas le parcours en cas d'échec :</b> le verrou est une
   * protection, pas une condition d'accès. Si le réseau échoue ou si le créneau vient d'être
   * tenu, le patient peut continuer — mais on le prévient que sa place n'est pas garantie.
   * Bloquer le tunnel sur un échec de verrou rendrait l'application inutilisable au moindre
   * incident réseau.
   */
  const holdSlot = useCallback(async (slot: TimeSlot): Promise<string | null> => {
    if (!slot?.id) return null;

    // Un verrou déjà posé sur un AUTRE créneau doit être libéré : sinon le patient tiendrait
    // deux places alors qu'il n'en veut qu'une, et bloquerait les autres pour rien.
    const precedent = slotHoldRef.current;
    if (precedent && precedent.slotId !== slot.id) {
      try {
        await slotsApi.release(precedent.slotId);
      } catch {
        // Sans conséquence : le verrou précédent expirera de lui-même.
      }
      slotHoldRef.current = null;
      setSlotHold(null);
    }

    try {
      const response = await slotsApi.hold(slot.id);
      const data = response.data as unknown as SlotHoldResponse;
      const verrou: SlotHold = {
        holdId: data.holdId,
        slotId: data.slotId ?? slot.id,
        expiresAt: data.expiresAt,
        remainingSeconds: data.remainingSeconds,
        status: data.status,
      };
      slotHoldRef.current = verrou;
      setSlotHold(verrou);
      return null;
    } catch (error) {
      const data = (error as { response?: { data?: { error?: string; message?: string } } })
        ?.response?.data;
      const message =
        data?.error ||
        data?.message ||
        "Votre créneau n'a pas pu être réservé temporairement. Il reste disponible pour les autres patients.";
      slotHoldRef.current = null;
      setSlotHold(null);
      return message;
    }
  }, []);

  /**
   * Libère le verrou courant.
   *
   * <p>Appelé au retour arrière et au changement de créneau : sans cela, la place resterait
   * bloquée pour les autres patients jusqu'à l'expiration — cinq minutes de gêne sans raison.
   */
  const releaseHold = useCallback(async () => {
    const verrou = slotHoldRef.current;
    slotHoldRef.current = null;
    setSlotHold(null);
    if (!verrou) return;
    try {
      await slotsApi.release(verrou.slotId);
    } catch {
      // Le verrou expirera de lui-même : une libération en échec n'est pas bloquante.
    }
  }, []);

  /**
   * Oublie le verrou localement, sans appel serveur.
   *
   * <p>Après une réservation réussie, le serveur a déjà fermé le verrou en {@code CONSUMED}
   * dans la même transaction : un {@code DELETE} supplémentaire renverrait 404 pour rien.
   */
  const forgetHold = useCallback(() => {
    slotHoldRef.current = null;
    setSlotHold(null);
  }, []);
  const [preConsultationAnswers, setPreConsultationAnswers] = useState<Record<string, string>>(
    restored?.preConsultationAnswers ?? {}
  );
  const [reimbursementEstimation, setReimbursementEstimation] = useState<ReimbursementEstimation | null>(null);
  const [bookingConfirmed, setBookingConfirmed] = useState(false);
  const [bookingId, setBookingId] = useState<string | null>(null);

  /**
   * Issue #69 : chaque entree d'etape est transmise au backend, qui incremente un compteur
   * Prometheus. Sans cela, aucune donnee n'existe sur l'endroit ou les patients abandonnent.
   *
   * L'evenement ne porte que le nom de l'etape — aucune donnee personnelle.
   */
  useEffect(() => {
    if (currentStep === 'CONFIRMATION') return;
    trackStepEntered(currentStep as never);
  }, [currentStep]);

  /**
   * Sauvegarde du tunnel à chaque changement utile.
   *
   * On ne persiste PAS l'étape CONFIRMATION : un rendez-vous déjà créé ne doit pas
   * être re-proposé après un rechargement, sinon le patient croirait devoir confirmer
   * deux fois.
   */
  useEffect(() => {
    if (currentStep === 'CONFIRMATION') {
      clearPersisted();
      return;
    }
    if (currentStep === 'SEARCH' && !selectedDoctor && !selectedSlot) {
      clearPersisted();
      return;
    }
    savePersisted({
      currentStep,
      searchParams,
      selectedDoctor,
      selectedDate: selectedDate ? selectedDate.toISOString() : null,
      availableDates,
      selectedSlot,
      consultationType,
      selectedActTypeId,
      preConsultationAnswers,
    });
  }, [
    currentStep,
    searchParams,
    selectedDoctor,
    selectedDate,
    availableDates,
    selectedSlot,
    consultationType,
    selectedActTypeId,
    preConsultationAnswers,
  ]);

  // Step navigation
  //
  // Une seule source de vérité : `BOOKING_STEPS` (constante de module, donc stable). Une
  // liste recopiée ici finissait par diverger du type `BookingStep`, et `indexOf()` renvoyait
  // -1 pour une étape ajoutée d'un côté seulement — la navigation cessait alors silencieusement
  // d'avancer.
  const goToStep = useCallback((step: BookingStep) => {
    setCurrentStep(step);
  }, []);

  const nextStep = useCallback(() => {
    const currentIndex = BOOKING_STEPS.indexOf(currentStep);
    if (currentIndex < BOOKING_STEPS.length - 1) {
      setCurrentStep(BOOKING_STEPS[currentIndex + 1]);
    }
  }, [currentStep]);

  const prevStep = useCallback(() => {
    const currentIndex = BOOKING_STEPS.indexOf(currentStep);
    if (currentIndex > 0) {
      // Un retour arriere est un signal produit : le patient n'a pas trouve ce qu'il
      // cherchait a cette etape. On le compte comme abandon de l'etape quittee.
      trackAbandon(currentStep as never, 'BACK_NAVIGATION');
      // Quitter l'etape « creneau » ou « confirmation » libere la place tenue : sinon elle
      // resterait bloquee pour les autres patients jusqu'a expiration.
      if (currentStep === 'SLOTS' || currentStep === 'CONFIRMATION') {
        void releaseHold();
        setSelectedSlot(null);
      }
      setCurrentStep(BOOKING_STEPS[currentIndex - 1]);
    }
  }, [currentStep, releaseHold]);

  // Reset workflow
  const reset = useCallback(() => {
    // Un reset doit aussi libérer la place tenue : sinon le patient abandonne et bloque
    // les autres patients jusqu'à l'expiration.
    void releaseHold();
    clearPersisted();
    setCurrentStep('SEARCH');
    setSearchParams(null);
    setSelectedDoctor(null);
    setSelectedDate(null);
    setAvailableDates({});
    setSelectedSlot(null);
    // L'acte et le canal font partie du parcours : un nouveau parcours repart sans choix.
    setSelectedActTypeId(null);
    setPreConsultationAnswers({});
    setReimbursementEstimation(null);
    setBookingConfirmed(false);
    setBookingId(null);
  }, [releaseHold]);

  // Set booking confirmed with optional id
  const setBookingConfirmedWithId = useCallback((confirmed: boolean, id?: string) => {
    setBookingConfirmed(confirmed);
    if (id) {
      setBookingId(id);
    }
  }, []);

  // Context value
  const value = useMemo<BookingContextType>(() => ({
    currentStep,
    goToStep,
    nextStep,
    prevStep,
    searchParams,
    setSearchParams,
    selectedDoctor,
    setSelectedDoctor,
    selectedDate,
    setSelectedDate,
    availableDates,
    setAvailableDates,
    selectedSlot,
    setSelectedSlot,
    slotHold,
    holdSlot,
    releaseHold,
    forgetHold,
    consultationType,
    setConsultationType,
    selectedActTypeId,
    setSelectedActTypeId,
    preConsultationAnswers,
    setPreConsultationAnswers,
    reimbursementEstimation,
    setReimbursementEstimation,
    bookingConfirmed,
    bookingId,
    setBookingConfirmed: setBookingConfirmedWithId,
    reset,
  }), [
    currentStep,
    goToStep,
    nextStep,
    prevStep,
    searchParams,
    selectedDoctor,
    selectedDate,
    availableDates,
    selectedSlot,
    slotHold,
    holdSlot,
    releaseHold,
    forgetHold,
    consultationType,
    selectedActTypeId,
    preConsultationAnswers,
    reimbursementEstimation,
    bookingConfirmed,
    bookingId,
    reset,
    setBookingConfirmedWithId,
  ]);

  return (
    <BookingContext.Provider value={value}>
      {children}
    </BookingContext.Provider>
  );
}

/**
 * Hook to use the booking context
 * 
 * @throws Will throw if used outside of BookingProvider
 * 
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { selectedDoctor, nextStep } = useBooking();
 *   // ...
 * }
 * ```
 */
export function useBooking(): BookingContextType {
  const context = useContext(BookingContext);
  if (!context) {
    throw new Error('useBooking must be used within a BookingProvider');
  }
  return context;
}

// Helper hooks
export function useCurrentStep() {
  const context = useContext(BookingContext);
  if (!context) {
    throw new Error('useCurrentStep must be used within a BookingProvider');
  }
  return context.currentStep;
}

export function useBookingNavigation() {
  const context = useContext(BookingContext);
  if (!context) {
    throw new Error('useBookingNavigation must be used within a BookingProvider');
  }
  return {
    currentStep: context.currentStep,
    goToStep: context.goToStep,
    nextStep: context.nextStep,
    prevStep: context.prevStep,
  };
}

